import axios from "axios";
import { useContext, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { PrimaryButtonLarge } from "../../../components/Buttons/Buttons";
import { SectionTitle } from "../../../components/Form/Form";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import { SampleRequestItem } from "../../../components/TransactionItem/SampleRequestItem";
import { Title } from "../../../components/Typography/Typography";
import { calculateTotalQuantity } from "../../../components/quoteCart/BuyerQuoteItemForm";
import { Form } from "../../../layout/FormLayout";
import { Card } from "../../../layout/portalPageLayout";
import type {
  CreateSampleRequest,
  CurrencyCode,
  DecisionTimeline,
  Fee,
  ITenantCustomerSettings,
  OptionType,
  ProductApplication,
  ProductSKU,
  RequestableDocuments,
  SampleRequestDetail,
  Tenant,
  TransactionPlacedVia,
  UUID,
} from "../../../types/types";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import type { PIMProductBase } from "../../../types/types.PIM";
import { packagingTypeOrUnitToOption } from "../../../util/SkuUtils";
import type {
  POCFormSchemaType,
  POCManualFormSchemaType,
} from "../../../util/util";
import {
  addressToOption,
  convertApplicationToOption,
  convertDeliveryTermToOption,
  convertPaymentModeToOption,
  convertPaymentTermToOption,
  convertProductSKUToOption,
  isAxiosError,
  useCurrency,
  useFormWrapper,
  useStoreState,
} from "../../../util/util";
import type { AddOrEditProductFormOutput } from "./AddOrEditSampleProductForm";
import { AddOrEditSampleProductForm } from "./AddOrEditSampleProductForm";
import { CreateSampleRequestBottomForm } from "./CreateSampleRequestBottomForm";
import { strings } from "../../../util/strings";
import {
  RequestAddress,
  RequestAddressSchemaType,
} from "../../public/RequestAddress/RequestAddress";
import { PointOfContact } from "../../SharedPages/OrganizationPage/CompanyInfo/Edit/PointOfContactForm";
import type { LocationInputs } from "../../SharedPages/OrganizationPage/CompanyInfo/Edit/LocationForm";
import { useAddLocation } from "../../SharedPages/OrganizationPage/CompanyInfo/Edit/EditLocations";
import { getUserDetailsFromTenant } from "../SellerCreation.constants";
import { lineHeightTokens } from "../../../designTokens";
import { TextXsRegular } from "../../../components/Typography/TypographyV2";
import { formatPrice } from "../../../util/util-components";
import { QuoteFees } from "../SellerCreateQuote/QuoteFees";
import { CustomerDetailsForm } from "../SellerCreateOrder/CustomerDetailsForm";
import type { BuyerOption } from "../SellerCreateOrder/CustomerDetailsForm.utils";
import { isShippingPointOfContactMissing } from "../SellerCreateOrder/CustomerDetailsForm.utils";
import { CreateSampleTermsForm } from "./CreateSampleBottomForm";

const Container = styled.section`
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-gap: 36px;
`;
const CardNoMargin = styled(Card)`
  margin-top: 0;
`;

const SubTotalSection = styled.div`
  display: grid;
  width: 100%;
  grid-template-areas: ". subtotal value";
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const SubTotalTitle = styled(TextXsRegular)`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  justify-self: flex-end;
  grid-area: subtotal;
`;

const SubTotalValue = styled(TextXsRegular)`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  justify-self: flex-end;
  grid-area: value;
`;

const TermsAndFeesContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const FormSection = styled.section`
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  &:nth-child(0) {
    margin-bottom: ${({ theme }) => theme.resolve("spacing_5xl")} !important;
  }
  &:nth-child(1) {
    margin-bottom: 0 !important;
  }
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const MainSection = styled.section`
  overflow-y: auto;
`;

const OrderItemArea = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_5xl")};
`;

const AddressWrapper = styled(OrderItemArea)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  max-width: 500px;
`;

const SectionTitleNoMargin = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.resolve("spacing_none")};
`;

const TitleReset = styled(Title)`
  margin-inline-start: ${({ theme }) => theme.resolve("spacing_none")};
  margin-block-start: 0;
  margin-inline-end: ${({ theme }) => theme.resolve("spacing_none")};
  font-size: ${({ theme }) => theme.fontSizes.xl};
`;

export interface SampleItemForDisplay extends SampleRequestDetail {
  entire_product: PIMProductBase;
  packaging_unit: string;
  sku: ProductSKU;
  purpose: string;
  product_id: string;
  applications: ProductApplication[];
}

type CustomerDetailsFormOutput = RequestAddressSchemaType & {
  buyer_id: BuyerOption;
  sample_request_via: OptionType<TransactionPlacedVia>;
  shipping_address_id: OptionType<UUID>;
  shipping_point_of_contact_id: OptionType<UUID>;

  order_place_via: OptionType<string>;
  delivery_term: OptionType<UUID>;
  payment_term: OptionType<UUID>;
};

interface CustomerDetailsPOCManualForm
  extends CustomerDetailsFormOutput,
    POCManualFormSchemaType {}

interface CustomerDetailsPOCForm
  extends CustomerDetailsFormOutput,
    POCFormSchemaType {}

export type CreateSampleRequestBottomFormOutput = RequestAddressSchemaType & {
  confirmation: boolean;
  is_tax_exempt: boolean;
  valid_to_date: string;
  required_eta: string;
  payment_method: OptionType<UUID>;
  feesList?: Fee[];
  discount_code: string | null;

  enable_payments: boolean;
  is_decision_maker: OptionType<boolean>;
  documents: { id: RequestableDocuments; name: RequestableDocuments }[];
  decision_timeline: { name: DecisionTimeline };
};

export function SellerCreateSampleRequest({
  onSuccess,
}: {
  onSuccess: (sampleID: UUID) => void;
}) {
  const { t } = useTranslation();
  const {
    tenant_id,
    storefront_id,
    storefront_metadata: { enable_payments_on_sample_requests },
  } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const [selectedBuyer, setSelectedBuyer] = useState<Tenant | null>(null);
  const [sampleItems, setSampleItems] = useState<Array<SampleItemForDisplay>>(
    []
  );
  const [customerSettings, setCustomerSettings] =
    useState<ITenantCustomerSettings | null>(null);

  const [pointOfContactType, setPointOfContactType] =
    useState<"manual" | "existing">("existing");
  const customerDetailsIsDisabled = sampleItems.length > 0;
  const [product, setProduct] = useState<PIMProductBase | null>(null);
  const [formData, setFormData] = useState<{
    customer: CustomerDetailsFormOutput | null;
    bottomForm: CreateSampleRequestBottomFormOutput | null;
  }>({ customer: null, bottomForm: null });
  const [selectedItemIndex, setSelectedItemIndex] =
    useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const isEditMode = selectedItemIndex !== null;
  const addLocation = useAddLocation({
    buyer_tenant_id: selectedBuyer?.id ?? "",
  });

  const customerDetailsMethods = useFormWrapper<CustomerDetailsFormOutput>();

  const productInfoMethods = useFormWrapper<AddOrEditProductFormOutput>({
    defaultValues: {
      packaging: null,
      product_id: undefined,
    },
  });

  const { watch: productInfoWatch } = productInfoMethods;
  const watchedPaymentToggle = productInfoWatch("enable_payments");

  const bottormOrderFormSchema = Yup.lazy((formValues: any) =>
    Yup.object().shape({
      feesList: Yup.array(
        Yup.object().shape({
          name: Yup.string(),
          amount: Yup.number()
            .min(0, t("Must be an integer value"))
            .typeError(strings(t).thisIsARequiredField)
            .required(strings(t).thisIsARequiredField),
        })
      ),
      ...(watchedPaymentToggle && {
        payment_method: Yup.object()
          .shape({
            value: Yup.string().required(strings(t).thisIsARequiredField),
            label: Yup.string().required(strings(t).thisIsARequiredField),
          })
          .nullable()
          .required(strings(t).thisIsARequiredField),
        valid_to_date: Yup.string()
          .nullable()
          .required(t("Price valid until is required")),
        required_eta: Yup.string()
          .nullable()
          .required(t("Shipment date is required")),
      }),
      decision_timeline: Yup.object()
        .shape({
          name: Yup.string().required(strings(t).thisIsARequiredField),
        })
        .required(strings(t).thisIsARequiredField),
      is_decision_maker: Yup.object()
        .shape({
          value: Yup.boolean().required(strings(t).thisIsARequiredField),
          label: Yup.string().required(strings(t).thisIsARequiredField),
        })
        .nullable()
        .required(strings(t).thisIsARequiredField),
    })
  );

  const bottomFormMethods = useFormWrapper({
    resolver: yupResolver(bottormOrderFormSchema),
    defaultValues: {
      decision_timeline: { name: "" },
      confirmation: false,
      documents: [],
      feesList: !Boolean(customerSettings?.is_tax_exempt)
        ? [{ name: "Tax", amount: 0, id: "tax_fee" }]
        : [{ name: "", amount: 0 }],
    },
  });

  const { getValues, setError } = bottomFormMethods;

  const { watch: customerDetailsWatch } = customerDetailsMethods;
  const { reset: productInfoReset, setValue: setProductValue } =
    productInfoMethods;

  const allCustomerDetailsValues = customerDetailsWatch();

  const { shipping_address_id: destinationOption, buyer_id: buyerOption } =
    allCustomerDetailsValues;

  const productFormIsDisabled =
    !(!!destinationOption && !!buyerOption) ||
    isShippingPointOfContactMissing({
      buyer: selectedBuyer,
      shippingAddressOption: destinationOption,
      pointOfContactOption:
        allCustomerDetailsValues.shipping_point_of_contact_id,
    });

  const bottomFormOnSubmit = async <
    T extends CreateSampleRequestBottomFormOutput
  >(
    output: T
  ) => {
    if (!loading) {
      setLoading(true);
      setFormData((data) => ({ ...data, bottomForm: output }));
      let newAddress: OptionType<string> | null = null;
      let newAddressPointOfContactId: string | undefined;
      if (destinationOption?.value === "new_address" && formData.customer) {
        const customerDetails = formData.customer;
        const inputs: LocationInputs = {
          company_name: customerDetails?.company_name ?? "",
          external_id: customerDetails?.external_id ?? "",
          address1: customerDetails.address1,
          address2: customerDetails.address2,
          country: customerDetails.country,
          county: customerDetails.county,
          state: customerDetails.state,
          city: customerDetails.city,
          postal_code: customerDetails.postal_code,
          is_active: true,
          points_of_contact: [
            pointOfContactType === "manual"
              ? {
                  poc_type: "manual",
                  contact_first_name: (
                    customerDetails as CustomerDetailsPOCManualForm
                  ).contact_first_name,
                  contact_last_name: (
                    customerDetails as CustomerDetailsPOCManualForm
                  ).contact_last_name,
                  email_address: (
                    customerDetails as CustomerDetailsPOCManualForm
                  ).email_address,
                  phone_number: (
                    customerDetails as CustomerDetailsPOCManualForm
                  ).phone_number,
                  country_code: (
                    customerDetails as CustomerDetailsPOCManualForm
                  ).country_code,
                }
              : {
                  poc_type: "existing",
                  point_of_contact_id: (
                    customerDetails as CustomerDetailsPOCForm
                  ).point_of_contact_id,
                },
          ],
        };
        const newLocation = await addLocation({ inputs });
        if (newLocation) {
          newAddress = addressToOption(newLocation);
          newAddressPointOfContactId = newLocation.points_of_contact?.[0]?.id;
        }
      }
      const hasAddress =
        destinationOption?.value === "new_address"
          ? Boolean(newAddress)
          : Boolean(destinationOption?.value);
      if (hasAddress) {
        try {
          if (formData.customer) {
            const object: CreateSampleRequest = {
              transaction_state: "sample_accepted",
              data: {
                buyer_id: formData.customer.buyer_id.tenant_id,
                tenant_id: tenant_id,
                sample_placed_via: formData.customer.sample_request_via?.value,
                shipping_address_id: newAddress
                  ? newAddress.value
                  : formData.customer?.shipping_address_id.value,
                shipping_point_of_contact_id: newAddress
                  ? newAddressPointOfContactId ?? null
                  : formData.customer?.shipping_point_of_contact_id?.value ??
                    null,
                is_decision_maker: output?.is_decision_maker.value,
                decision_timeline: output.decision_timeline.name,
                requested_documents: output.documents.map((doc) => doc.name),
                ...(enable_payments_on_sample_requests && {
                  payment_mode_id: output.payment_method?.value,
                  delivery_term_id: formData.customer.delivery_term.value,
                  is_tax_exempt: output.is_tax_exempt,
                  required_eta: output.required_eta,
                  valid_to_date: output.valid_to_date,
                  payment_term_id: formData.customer.payment_term.value,
                  fees_to_add: output.feesList
                    ? [
                        ...output.feesList.reduce((fees: Fee[], fee) => {
                          if (!!fee.amount) {
                            const { id, name, amount } = fee;
                            fees.push({
                              id,
                              name,
                              amount,
                              currency: currencyCode || ("USD" as CurrencyCode),
                            });
                          }
                          return fees;
                        }, []),
                      ]
                    : [],
                }),
                items: sampleItems.map((item) => ({
                  product_id: item.product_id,
                  purpose: item.purpose,
                  no_of_units: item.no_of_units,
                  total_quantity: item.total_quantity,
                  total_quantity_packaging_unit_id:
                    item.total_quantity_packaging_unit?.id,
                  applications: item.applications[0]?.id,
                  sample_quantity: item.sample_quantity,
                  enable_payments: item.enable_payments ?? false,
                  ...(item.custom_application
                    ? { custom_application: item.custom_application }
                    : {}),
                  ...(item.price_per_unit && enable_payments_on_sample_requests
                    ? { price_per_unit: item.price_per_unit }
                    : {}),
                  sku: {
                    id: item.sku.id,
                  },
                })),
                buyer_user_id: formData.customer.buyer_id.value,
              },
            };
            const sample = await axios.post(
              `/v1/storefronts/${storefront_id}/transactions/create-sample`,
              object
            );
            notifySuccess(t("Sample Request recorded successfully"));
            setLoading(false);
            onSuccess(sample.data.id);
          } else {
            throw Error(
              "Unexpectedly couldn't create sample request on frontend"
            );
          }
        } catch (error) {
          if (
            isAxiosError(error) &&
            error?.response?.data?.status_code === "403"
          ) {
            notifyError(error.response.data.message);
          } else {
            notifyError(t("There was an error creating the sample request"), {
              error,
            });
          }
          if (newAddress) {
            try {
              await axios.delete(
                `/v1/tenants/${selectedBuyer!.id}/addresses/${newAddress.value}`
              );
            } catch (_) {
              notifyError(t("There was an error cleaning up the address."));
            }
          }
          console.error(error);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
  };

  const customerDetailsOnSubmit = async (output: CustomerDetailsFormOutput) => {
    setFormData((data) => ({ ...data, customer: output }));
    bottomFormMethods.handleSubmit(bottomFormOnSubmit);
  };

  const productDetailsOnSubmit = async (input: AddOrEditProductFormOutput) => {
    const theProduct = product as PIMProductBase;
    if (input.packaging) {
      let object: SampleItemForDisplay = {
        packaging_unit: input.packaging.value.packaging_unit.id,
        product: {
          name: theProduct.name,
          identifiers: theProduct?.identifiers,
          // included for compatibility reasons
          safety_datasheet: "",
          technical_datasheet: "",
        },
        documents: [],
        entire_product: theProduct,
        sku: input.packaging.value,
        purpose: input.purpose.label,
        product_id: theProduct.id,
        id: theProduct.id,
        no_of_units: input.no_of_units,
        total_quantity: input.total_annual_quantity,
        price_per_unit: input.price_per_unit,
        enable_payments: !!input.enable_payments,
        sample_quantity_packaging_unit: {
          name: input.packaging.value.packaging_unit.name,
          id: input.packaging.value.packaging_unit.id,
        },
        total_quantity_packaging_unit: {
          name: input.total_quantity_unit.label,
          id: input.total_quantity_unit.value,
        },
        sample_quantity: String(
          calculateTotalQuantity(
            input.packaging.value.package_volume,
            input.no_of_units
          )
        ),
        ...(input.application && input.application.value
          ? {
              applications: [
                {
                  name: input.application.label as string,
                  id: input.application.value as string,
                  image_url: null,
                },
              ],
            }
          : { applications: [] }),
        ...(input.custom_application
          ? { custom_application: input.custom_application }
          : {}),
        currency: currencyCode ?? "USD",
      };

      // form works for edit and create
      const index = sampleItems.findIndex(
        (item) => item.product_id === input.product_id
      );

      if (index >= 0) {
        setSampleItems((items) =>
          items.map((item, idx) => {
            if (idx === index) {
              return object;
            } else return item;
          })
        );
      } else {
        setSampleItems((items) => [...items, object]);
      }

      clearProductForm();
      setSelectedItemIndex(null);
      clearProductForm();
      setProductValue(
        "enable_payments",
        sampleItems.length > 0
          ? !!sampleItems[0].enable_payments
          : !!input.enable_payments
      );
    }

    return null;
  };

  const currencyCode = customerSettings?.preferred_currency ?? "USD";
  const currency = useCurrency(currencyCode ?? "USD")!;

  const updateTaxFeeRef =
    useRef<{ updateTaxFee: (isTaxExempt: boolean) => void }>(null);

  const clearProductForm = (): void => {
    productInfoReset();
    setProductValue("packaging", { value: undefined, label: undefined });
    setProductValue("product_id", null);
    setProduct(null);
    setProductValue("application", null);
    setProductValue("total_quantity_unit", null);
    setProductValue("purpose", null);
  };

  /**
   * Prefill the product form in edit mode.
   * @param item
   */
  const prefillProductForm = (item: SampleItemForDisplay): void => {
    setProduct(item.entire_product);
    setProductValue("product_id", item.entire_product.id);
    setProductValue(
      "application",
      item.applications[0]
        ? convertApplicationToOption(item.applications[0])
        : null
    );
    setProductValue("packaging", convertProductSKUToOption(item.sku));
    setProductValue("price_per_unit", item.price_per_unit);
    setTimeout(() => {
      setProductValue("total_quantity", item.total_quantity);
      setProductValue("no_of_units", item.no_of_units);

      setProductValue(
        "total_quantity_unit",
        item.total_quantity_packaging_unit
          ? packagingTypeOrUnitToOption(item.total_quantity_packaging_unit)
          : null
      );
      setProductValue("purpose", { value: item.purpose, label: item.purpose });
      setProductValue("total_annual_quantity", item.total_quantity); // probably correct??
    });
  };

  const onCreateSampleRequest = () => {
    if (!getValues("decision_timeline").name) {
      setError("decision_timeline", {
        type: "required",
        message: strings(t).thisIsARequiredField,
      });
    }
    customerDetailsMethods.handleSubmit(customerDetailsOnSubmit)();
  };

  return (
    <>
      {/* Title is larger than design but consistent with app. */}
      <TitleReset>{t("Record a Sample")}</TitleReset>
      <Container>
        <FormSection>
          <CardNoMargin>
            {/* "Create Order Form" */}
            <FormProvider {...customerDetailsMethods}>
              <Form
                noValidate
                onSubmit={customerDetailsMethods.handleSubmit(
                  customerDetailsOnSubmit
                )}
              >
                <SectionTitleNoMargin>
                  {t("Customer Details")}
                </SectionTitleNoMargin>
                <CustomerDetailsForm
                  setSelectedBuyer={setSelectedBuyer}
                  setCustomerSettings={setCustomerSettings}
                  isDisabled={customerDetailsIsDisabled}
                  orderType="sample"
                />
              </Form>
            </FormProvider>
          </CardNoMargin>
          <CardNoMargin selectedBorder={isEditMode}>
            {/* "Create/Edit Product Form" */}
            <FormProvider {...productInfoMethods}>
              <Form
                onSubmit={productInfoMethods.handleSubmit(
                  productDetailsOnSubmit
                )}
                noValidate
              >
                <SectionTitleNoMargin>
                  {t("Choose Products")}
                </SectionTitleNoMargin>
                <AddOrEditSampleProductForm
                  isDisabled={productFormIsDisabled}
                  // fallback is displayed before a buyer is selected.
                  product={product}
                  currencyCode={currencyCode}
                  setProduct={setProduct}
                  isEditMode={isEditMode}
                  onCancel={() => {
                    setSelectedItemIndex(null);
                    clearProductForm();
                  }}
                  // This will exist by the time this form is interactive.
                  buyer_tenant_id={selectedBuyer?.id as UUID}
                  lockEnablePayments={sampleItems.length > 0}
                />
              </Form>
            </FormProvider>
          </CardNoMargin>
        </FormSection>
        <MainSection>
          {sampleItems.length > 0 ? (
            <CardNoMargin>
              {/* Cart section */}
              {/* Manually add 15px because the Form component does this for customer details */}
              <SectionTitleNoMargin style={{ marginBottom: "15px" }}>
                {t("Sample Summary")}
              </SectionTitleNoMargin>
              <OrderItemArea>
                {sampleItems.map((item, index) => {
                  return (
                    <SampleRequestItem
                      item={item}
                      handleConfirmDelete={(index) => {
                        setSampleItems((items) => {
                          return items.filter((item, idx) => index !== idx);
                        });
                      }}
                      handleEdit={() => {
                        setSelectedItemIndex(index);
                        prefillProductForm(item);
                      }}
                      index={index}
                      key={index}
                    />
                  );
                })}
              </OrderItemArea>

              <FormProvider {...bottomFormMethods}>
                {enable_payments_on_sample_requests && (
                  <SubTotalSection>
                    <SubTotalTitle>{t("Subtotal")}</SubTotalTitle>
                    <SubTotalValue>
                      {" "}
                      {formatPrice(
                        sampleItems.reduce(
                          (acc, item) =>
                            (acc +=
                              parseFloat(item.sample_quantity) *
                              parseFloat(item.price_per_unit ?? "0")),
                          0
                        ),
                        customerSettings?.preferred_currency ?? "USD"
                      )}
                    </SubTotalValue>
                  </SubTotalSection>
                )}
                {Boolean(destinationOption) &&
                destinationOption?.value === "new_address" &&
                Boolean(selectedBuyer) ? (
                  <AddressWrapper>
                    <RequestAddress
                      methodsOfUseForm={customerDetailsMethods}
                      hideBillingAddress
                      showBillingAddressOption={false}
                      showLocationId={true}
                      show_tax_id={true}
                      formTitle={t("Add new address")}
                    />
                    <PointOfContact
                      methodsOfUseForm={customerDetailsMethods}
                      pointOfContactType={pointOfContactType}
                      setPointOfContactType={setPointOfContactType}
                      defaultExistingUser={getUserDetailsFromTenant(
                        selectedBuyer!
                      )}
                    />
                  </AddressWrapper>
                ) : (
                  <></>
                )}
                {watchedPaymentToggle && (
                  <TermsAndFeesContainer>
                    <div style={{ flex: 1 }}>
                      <>
                        <SectionTitleNoMargin>
                          {t("Your Terms")}
                        </SectionTitleNoMargin>
                        <Form
                          onSubmit={bottomFormMethods.handleSubmit(
                            bottomFormOnSubmit
                          )}
                        >
                          <CreateSampleTermsForm
                            isTaxExempt={
                              customerSettings?.is_tax_exempt ?? false
                            }
                            showAllFields={false}
                            updateTaxFee={(isTaxExempt) =>
                              updateTaxFeeRef.current?.updateTaxFee(isTaxExempt)
                            }
                            defaultPaymentMethod={
                              customerSettings?.default_payment_mode
                                ? convertPaymentModeToOption(
                                    customerSettings?.default_payment_mode
                                  )
                                : null
                            }
                            defaultDeliveryTerm={
                              customerSettings?.default_delivery_term
                                ? convertDeliveryTermToOption(
                                    customerSettings?.default_delivery_term
                                  )
                                : null
                            }
                            defaultPaymentTerm={
                              customerSettings?.default_payment_term
                                ? convertPaymentTermToOption(
                                    customerSettings?.default_payment_term
                                  )
                                : null
                            }
                          />
                        </Form>
                      </>
                    </div>
                    <div style={{ flex: 1 }}>
                      <QuoteFees
                        methodsOfUseForm={bottomFormMethods}
                        subtotal={sampleItems.reduce(
                          (acc, item) =>
                            (acc +=
                              parseFloat(item.sample_quantity) *
                              parseFloat(item.price_per_unit ?? "0")),
                          0
                        )}
                        currency={currency}
                        ref={updateTaxFeeRef}
                        showDiscounts={false}
                        buyerTenantId={selectedBuyer?.id}
                        handleValidDiscountCode={() => {}}
                      />
                    </div>
                  </TermsAndFeesContainer>
                )}
              </FormProvider>

              <FormProvider {...bottomFormMethods}>
                <SectionTitleNoMargin>{t("Details")}</SectionTitleNoMargin>
                <>
                  <Form
                    noValidate
                    onSubmit={bottomFormMethods.handleSubmit(
                      bottomFormOnSubmit
                    )}
                  >
                    <CreateSampleRequestBottomForm />
                    <PrimaryButtonLarge
                      loading={loading}
                      type="submit"
                      onClick={onCreateSampleRequest}
                    >
                      {t("Create Sample Request")}
                    </PrimaryButtonLarge>
                  </Form>
                </>
              </FormProvider>
              {/* Submit cart form. */}
            </CardNoMargin>
          ) : (
            // TODO correct font size for body when it exists
            <CardNoMargin
              style={{
                lineHeight: lineHeightTokens.line_height_xl,
                fontSize: "19px",
              }}
            >
              <SectionTitleNoMargin>{t("Sample Summary")}</SectionTitleNoMargin>
              <p>{t("To record a sample follow these steps")}</p>
              <ol type="1">
                <li>{t("Choose your customer and review the details")}</li>
                <li>{t("Add products to the cart")}</li>
                <li>{t("Review the cart and create sample request")}</li>
              </ol>
            </CardNoMargin>
          )}
        </MainSection>
      </Container>
    </>
  );
}